import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { UsersService } from '@features/users/services/users.service';
import { ModalController } from '@ionic/angular';
import { SendAgreementForSignaturesComponent } from '@shared/components/send-agreement-for-signatures/send-agreement-for-signatures.component';
import { Session } from '@shared/models/session';
import { RouterService } from '@shared/services/router.service';
import { SessionService } from '@shared/services/session.service';
import { ToastService } from '@shared/services/toast.service';
import { LoadingState } from '@shared/types/loading-state';
import { Permission } from '@shared/types/permission';
import { Observable, BehaviorSubject, skipWhile, take, withLatestFrom, map, pairwise, startWith } from 'rxjs';
import moment from 'moment';
import { addIcons } from 'ionicons';
import * as icons from '@shared/constants/icons';
@Component({
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  confirmEmailLoadingState$: Observable<LoadingState>;
  sessionLoadingState$: Observable<LoadingState>;
  sessionLoadingText$: Observable<string | undefined>;
  session$: Observable<Session | undefined>;
  permissions$: Observable<Permission[] | undefined>;
  limitedAccessAllowed$: Observable<boolean | undefined>;
  limitedAccessDaysLeft$: Observable<number | undefined>;
  currentUrl$: Observable<string | undefined>;
  sectionTransitionName$: Observable<string>;
  isConfirmPartnerDetailsRequired: boolean = false;
  collapsedAlertsCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  totalAlertsCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  alertsVisibility: Record<string, { hidden: boolean; active: boolean }> = {
    email: {
      hidden: false,
      active: false,
    },
    agreement: {
      hidden: false,
      active: false,
    },
    mfa: {
      hidden: false,
      active: false,
    },
    confirmPartnerDetails: {
      hidden: false,
      active: false,
    },
  };

  constructor(
    private sessionService: SessionService,
    private usersService: UsersService,
    private routerService: RouterService,
    private modalController: ModalController,
    private toastService: ToastService,
  ) {
    addIcons(icons);
  }

  ngOnInit(): void {
    this.session$ = this.sessionService.getSession();
    this.permissions$ = this.sessionService.getSessionPermissions();
    this.sessionLoadingState$ = this.sessionService.getSessionLoading();
    this.sessionLoadingText$ = this.sessionService.getSessionLoadingText();
    this.limitedAccessAllowed$ = this.sessionService.getLimitedAccessAllowed();
    this.limitedAccessDaysLeft$ = this.sessionService.getLimitedAccessDaysLeft();
    this.sessionService.loadSession();
    this.confirmEmailLoadingState$ = this.usersService.getConfirmEmailLoading();
    this.currentUrl$ = this.routerService.getCurrentUrl();
    this.sectionTransitionName$ = this.currentUrl$.pipe(
      map((url) => (url || '').split('/')[1] || ''),
      pairwise(),
      map(([prev, curr]) => (prev && prev !== curr ? 'section-content' : 'none')),
      startWith('none'),
    );
    const oneYearAgo = moment.utc().subtract(1, 'year');
    this.session$
      .pipe(
        withLatestFrom(this.permissions$, this.currentUrl$),
        skipWhile(([session, permissions, currentUrl]) => !session || !permissions || !currentUrl),
        take(1),
      )
      .subscribe(([session, permissions, currentUrl]) => {
        const stalePartnerConfirmation = !!session!.groups[0].last_confirmed_at
          ? moment.utc(session!.groups[0].last_confirmed_at, 'YYYY-MM-DD HH:mm:ss [UTC]', true).isBefore(oneYearAgo)
          : false;

        if (
          (permissions!.includes('partner_details_edit') &&
            session!.groups[0].last_confirmed_at == null &&
            currentUrl !== '/settings/your-partner-details') ||
          stalePartnerConfirmation
        ) {
          this.isConfirmPartnerDetailsRequired = true;
        }
        this.alertsVisibility['email'].active = !session!.email_confirmed;
        this.alertsVisibility['agreement'].active =
          session!.user_type != 'intranet' && !session!.groups[0]?.groups_agreement?.signed_at;
        this.alertsVisibility['mfa'].active = session!.mfa_setting?.enforced && !session!.mfa_setting?.active;
        this.alertsVisibility['confirmPartnerDetails'].active = this.isConfirmPartnerDetailsRequired;

        this.updateTotalAlertsCount();
      });
  }

  updateTotalAlertsCount() {
    const activeAlertsCount = Object.values(this.alertsVisibility).filter((alert) => alert.active).length;
    this.totalAlertsCount$.next(activeAlertsCount);
  }

  collapseAlert(type: string) {
    this.alertsVisibility[type].hidden = true;
    this.collapsedAlertsCount$.next(this.collapsedAlertsCount$.value + 1);
    this.totalAlertsCount$.next(this.totalAlertsCount$.value - 1);
  }

  collapseAllAlerts() {
    Object.keys(this.alertsVisibility).forEach((key) => {
      this.alertsVisibility[key].hidden = true;
    });
    this.collapsedAlertsCount$.next(Object.values(this.alertsVisibility).filter((alert) => alert.active).length);
    this.totalAlertsCount$.next(0);
  }

  expandAllAlerts() {
    Object.keys(this.alertsVisibility).forEach((key) => {
      this.alertsVisibility[key].hidden = false;
    });
    this.collapsedAlertsCount$.next(0);
    this.updateTotalAlertsCount();
  }

  resendEmailConfirmation() {
    this.session$.pipe(take(1)).subscribe((session) => {
      this.usersService.resendEmailConfirmation(session?.id!, true);
    });
  }

  presentSendAgreementForSignaturesModal() {
    this.session$.pipe(withLatestFrom(this.permissions$), take(1)).subscribe(([session, permissions]) => {
      if (permissions?.includes('partner_details_edit')) {
        this.modalController
          .create({
            id: 'send-agreement-for-signatures',
            component: SendAgreementForSignaturesComponent,
            componentProps: {
              groupId: session?.groups[0]?.id,
            },
          })
          .then((modal) => modal.present());
      } else {
        this.toastService.showToast(
          'error',
          'You do not have permission to send agreements for signatures. Please contact your ESET administrator.',
        );
      }
    });
  }

  @HostListener('document:ionPopoverDidPresent')
  onPopoverDidPresent() {
    if (typeof document === 'undefined') return;
    document.body.classList.add('allow-scroll-with-popover');
    document.body.style.overflowY = 'auto';
    document.documentElement.style.overflowY = 'auto';
  }

  @HostListener('document:ionPopoverDidDismiss')
  onPopoverDidDismiss() {
    if (typeof document === 'undefined') return;
    document.body.classList.remove('allow-scroll-with-popover');
    document.body.style.removeProperty('overflow-y');
    document.documentElement.style.removeProperty('overflow-y');
  }
}
